'use client'

import React, { createContext, useMemo } from 'react'

import { useLocale } from '@rewardops/ui'

import { useApi } from '~/components/hooks/use-api'
import { useElpPromotions } from '~/components/hooks/use-elp-promotions'

type ElpPromotionsContextType = {
  hasActiveElpPromotions: boolean
  elpPromotionsLoading: boolean
  showPromotionsLink: boolean
}

export const ElpPromotionsContext = createContext<ElpPromotionsContextType>({
  hasActiveElpPromotions: false,
  elpPromotionsLoading: true,
  showPromotionsLink: false,
})

type ElpPromotionsProviderProps = {
  children: React.ReactNode
}

const ElpPromotionsProvider: React.FC<ElpPromotionsProviderProps> = ({
  children,
}: ElpPromotionsProviderProps) => {
  const { api, tenant } = useApi()
  const { locale } = useLocale()
  const isElpTenant = tenant?.theme === 'pointsAtWork'
  const language = locale || tenant?.locale?.default || 'en'

  const { data: elpOffers, isLoading: elpOffersLoading } =
    api.elpPromotions.getPromotions.useQuery(null, { enabled: isElpTenant })

  // getByTypeAndFilterByField always returns an array (never the "no data found" string
  // that getByType returns on a Redis cache miss)
  const { data: elpContent, isLoading: elpContentLoading } =
    api.contentful.getByTypeAndFilterByField.useQuery(
      {
        type: 'elpPromotionsContainer',
        language,
      },
      { enabled: isElpTenant && Boolean(language) },
    )

  const elpPromotionsLoading = elpOffersLoading || elpContentLoading

  const promotionsContent = useMemo(
    () =>
      Array.isArray(elpContent)
        ? elpContent.flatMap((container: any) => container?.promotions ?? [])
        : [],
    [elpContent],
  )

  // reuse the shared hook that merges ELP offers with Contentful content by instanceId
  const { promotions } = useElpPromotions({
    elpOffers: elpOffers?.offers,
    promotionsContent,
    offersLoading: elpPromotionsLoading,
  })

  const hasActiveElpPromotions =
    isElpTenant && !elpPromotionsLoading && promotions.length > 0

  // for non-ELP tenants the link is unaffected; for ELP tenants it shows
  // only once we know there is at least one active promotion (never during load)
  const showPromotionsLink = !isElpTenant || hasActiveElpPromotions

  const value = useMemo(
    () => ({
      hasActiveElpPromotions,
      elpPromotionsLoading,
      showPromotionsLink,
    }),
    [hasActiveElpPromotions, elpPromotionsLoading, showPromotionsLink],
  )

  return (
    <ElpPromotionsContext.Provider value={value}>
      {children}
    </ElpPromotionsContext.Provider>
  )
}

export { ElpPromotionsProvider }
